import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { jsPDF } from "jspdf";
import { brl, num, km } from "@/lib/format";
import { Package, Route, FileText, MessageCircle, Pencil, Check, X, RotateCcw } from "lucide-react";
import { useReportLogo, addPdfLogo } from "@/lib/useLogo";
import { ReportLogo } from "@/components/ReportLogo";
import { usePhones } from "@/components/ReceiptWhatsApp";
import { shareFiles } from "@/lib/payrollXlsx";
import { WhatsAppMenu } from "@/components/WhatsAppMenu";

const TONE = { pos: "text-emerald-300", neg: "text-rose-300", tax: "text-amber-300" };
const PDF_TONE = { pos: [16, 122, 87], neg: [190, 60, 60], tax: [180, 130, 40] };

export const buildDetailPdf = (d, periodLabel, existing, logo) => {
  const doc = existing || new jsPDF({ unit: "pt", format: "a4" });
  if (existing) doc.addPage();
  addPdfLogo(doc, logo, { top: 10 });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 40;
  let y = M;
  const line = (label, val, o = {}) => {
    if (y > H - 50) { doc.addPage(); y = M; }
    doc.setFontSize(o.size || 10);
    doc.setFont("helvetica", o.bold ? "bold" : "normal");
    doc.setTextColor(...(o.color || [40, 40, 40]));
    doc.text(String(label), o.x ?? M, y);
    if (val != null) doc.text(String(val), W - M, y, { align: "right" });
    y += o.gap || 15;
  };
  const rule = () => { doc.setDrawColor(220); doc.line(M, y - 6, W - M, y - 6); y += 8; };
  line("Central do Courier - Detalhamento do colaborador", null, { bold: true, size: 15, gap: 22, color: [20, 20, 20] });
  line(d.name, null, { bold: true, size: 13, gap: 16 });
  line(`${periodLabel}${d.type ? `  -  ${d.type}` : ""}`, null, { size: 10, color: [90, 90, 90], gap: 18 });
  rule();
  line("Entregas realizadas", num(d.deliveries), { bold: true });
  line("KM total", km(d.km), { bold: true, gap: 20 });
  if (d.sections) {
    rule();
    line("Componentes da folha", null, { bold: true, size: 11, gap: 18 });
    d.sections.forEach((s) => {
      const sign = s.tone === "pos" ? "" : "- ";
      line(s.title, `${sign}${brl(s.total)}`, { bold: true, size: 10, color: PDF_TONE[s.tone] });
      if (!s.items.length) line("    (sem itens)", "", { size: 9, color: [150, 150, 150] });
      s.items.forEach((it) => line(`    ${it.label}${it.tag ? ` (${it.tag})` : ""}`, `${sign}${brl(it.value)}`, { size: 9 }));
      y += 6;
    });
    rule();
    line("TOTAL LIQUIDO", brl(d.net), { bold: true, size: 12 });
  } else {
    line("Este colaborador nao esta na folha deste periodo.", null, { size: 10, color: [120, 120, 120] });
  }
  // Recibo: espaço para assinatura e data do colaborador.
  if (y > H - 170) { doc.addPage(); y = M; }
  y = Math.max(y + 30, H - 160);
  doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(70, 70, 70);
  doc.text(`Declaro que recebi e conferi o detalhamento acima referente ao periodo ${periodLabel}.`, M, y);
  y += 55;
  doc.setDrawColor(120);
  doc.line(M, y, M + 300, y);
  doc.line(W - M - 160, y, W - M, y);
  doc.setFontSize(8); doc.setTextColor(110, 110, 110);
  doc.text(`Assinatura do colaborador - ${d.name}`, M, y + 12);
  doc.text("/          /", W - M - 105, y - 4);
  doc.text("Data do recebimento", W - M - 160, y + 12);
  doc.setFontSize(8); doc.setTextColor(150, 150, 150);
  doc.text(`Gerado em ${new Date().toLocaleString("pt-BR")}`, M, H - 25);
  return doc;
};

const fileName = (name) => `detalhamento-${String(name || "colaborador").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-").toLowerCase()}.pdf`;

export const buildAllPdf = (list, periodLabel, logo) => {
  let doc = null;
  list.forEach((d) => { doc = buildDetailPdf(d, periodLabel, doc, logo); });
  return doc;
};

const waText = (d, periodLabel) => {
  const parts = (d.sections || []).map((s) => `${s.title}: ${s.tone === "pos" ? "" : "- "}${brl(s.total)}`);
  return [`Olá, ${d.name}! Segue o resumo da sua folha (${periodLabel}):`, `Entregas: ${num(d.deliveries)}`, ...parts, `Total líquido: ${d.sections ? brl(d.net) : "—"}`].join("\n");
};

// Envia o PDF anexado pelo menu de compartilhar do sistema (WhatsApp → contato).
const shareWhatsApp = (d, periodLabel, logo, phone) => {
  const name = fileName(d.name);
  const blob = buildDetailPdf(d, periodLabel, null, logo).output("blob");
  const text = waText(d, periodLabel);
  shareFiles([{ blob, name }], text, `https://wa.me/${phone || ""}?text=${encodeURIComponent(text)}`);
};

function ItemRow({ it, tone, editable, onEdit }) {
  const [v, setV] = useState(null);
  const canEdit = editable && it.id && !it.tag;
  const save = () => { onEdit(it.id, v); setV(null); };
  return (
    <div className={`flex items-center justify-between gap-2 rounded-md px-3 py-1.5 text-sm ${it.manual ? "border border-amber-500/40 bg-amber-500/5" : "bg-slate-950/60"}`} data-testid={`colab-item-${it.id || it.label}`}>
      <span className="truncate text-slate-300">{it.label}{it.tag && <span className="ml-1 rounded bg-slate-800 px-1 text-[10px] text-slate-400">{it.tag}</span>}
        {it.manual && <span className="ml-1 rounded bg-amber-500/15 px-1 text-[10px] text-amber-300" data-testid={`colab-item-manual-${it.id}`}>editado</span>}</span>
      {v != null ? (
        <span className="flex shrink-0 items-center gap-1">
          <input autoFocus type="number" step="0.01" min="0" value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setV(null); }}
            className="h-7 w-28 rounded border border-slate-600 bg-slate-950 px-2 text-right font-mono text-sm text-slate-100" data-testid={`colab-item-input-${it.id}`} />
          <button onClick={save} className="rounded p-1 text-emerald-300 hover:bg-slate-800" title="Salvar" data-testid={`colab-item-save-${it.id}`}><Check className="h-4 w-4" /></button>
          <button onClick={() => setV(null)} className="rounded p-1 text-slate-400 hover:bg-slate-800" title="Cancelar"><X className="h-4 w-4" /></button>
        </span>
      ) : (
        <span className="flex shrink-0 items-center gap-1">
          <span className={`font-mono ${it.manual ? "text-amber-200" : TONE[tone]}`}>{tone === "pos" ? "" : "- "}{brl(it.value)}</span>
          {canEdit && <button onClick={() => setV(String(Math.round((Number(it.value) || 0) * 100) / 100))} className="rounded p-1 text-slate-500 hover:text-orange-300" title="Editar valor deste colaborador" data-testid={`colab-item-edit-${it.id}`}><Pencil className="h-3.5 w-3.5" /></button>}
          {canEdit && it.manual && <button onClick={() => onEdit(it.id, null)} className="rounded p-1 text-slate-500 hover:text-rose-300" title="Voltar ao valor padrão" data-testid={`colab-item-reset-${it.id}`}><RotateCcw className="h-3.5 w-3.5" /></button>}
        </span>
      )}
    </div>
  );
}

function Section({ s, editable, onEdit }) {
  return (
    <div data-testid={`colab-detail-section-${s.key}`}>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{s.title}</span>
        <span className={`font-mono text-sm font-semibold ${TONE[s.tone]}`}>{s.tone === "pos" ? "" : "- "}{brl(s.total)}</span>
      </div>
      {s.items.length === 0 ? <p className="text-xs text-slate-600">Sem itens</p> : (
        <div className="space-y-1">
          {s.items.map((it, i) => <ItemRow key={`${it.label}-${i}`} it={it} tone={s.tone} editable={editable} onEdit={onEdit} />)}
        </div>
      )}
    </div>
  );
}

export function CollaboratorDetailDialog({ detail, periodLabel, onClose, onExcel, editable, onEditItem }) {
  const logo = useReportLogo();
  const phones = usePhones().data;
  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-slate-800 bg-slate-900 text-slate-100" data-testid="colab-detail-dialog">
        {detail && (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between gap-3 pr-6">
                <div className="flex min-w-0 items-center gap-3">
                  <ReportLogo className="h-8 max-w-[120px]" testId="colab-detail-logo" />
                  <DialogTitle className="text-xl text-slate-100" data-testid="colab-detail-name">{detail.name}</DialogTitle>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  <WhatsAppMenu testId="colab-detail-whatsapp" className="h-8 px-3 text-sm"
                    onPdf={() => shareWhatsApp(detail, periodLabel, logo, phones?.[detail.id])}
                    onExcel={() => onExcel?.(detail, waText(detail, periodLabel), phones?.[detail.id] || "")} />
                  <Button size="sm" variant="outline" className="h-8 border-slate-700 text-slate-300" data-testid="colab-detail-pdf"
                    onClick={() => buildDetailPdf(detail, periodLabel, null, logo).save(fileName(detail.name))}>
                    <FileText className="h-4 w-4" /> Baixar PDF
                  </Button>
                </div>
              </div>
              <DialogDescription className="text-slate-400">{periodLabel}{detail.type ? ` · ${detail.type}` : ""}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-slate-800 bg-slate-950 p-3">
                <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500"><Package className="h-3.5 w-3.5" /> Entregas</p>
                <p className="mt-1 font-mono text-2xl font-bold text-orange-300" data-testid="colab-detail-deliveries">{num(detail.deliveries)}</p>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-950 p-3">
                <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-500"><Route className="h-3.5 w-3.5" /> KM total</p>
                <p className="mt-1 font-mono text-2xl font-bold text-slate-100" data-testid="colab-detail-km">{km(detail.km)}</p>
              </div>
            </div>
            {detail.sections ? (
              <div className="space-y-4 border-t border-slate-800 pt-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Componentes da folha</p>
                {editable ? <p className="text-xs text-slate-500">Use o lápis para mudar o valor só deste colaborador nesta quinzena. O valor global não muda.</p>
                  : detail.sections.some((s) => s.items.some((it) => it.manual)) && <p className="text-xs text-slate-500">Valores em amarelo foram editados manualmente nas quinzenas.</p>}
                {detail.sections.map((s) => <Section key={s.key} s={s} editable={editable} onEdit={onEditItem} />)}
                <div className="flex items-center justify-between rounded-lg border border-orange-500/30 bg-orange-500/5 px-4 py-3">
                  <span className="font-semibold text-slate-200">Total líquido</span>
                  <span className="font-mono text-xl font-bold text-white" data-testid="colab-detail-net">{brl(detail.net)}</span>
                </div>
              </div>
            ) : (
              <p className="border-t border-slate-800 pt-4 text-sm text-slate-500" data-testid="colab-detail-no-payroll">Este colaborador não está na folha deste período.</p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
